import RandomIdGenerator from '@/utils/RandomIdGenerator';
import { P6_ID_TAG_PREFIX, makeP6IdTag, getCalendarP6Id } from '@/utils/P6CalendarMatch';
import { P6_TIMEZONE } from '@/utils/p6TimeZone';

/**
 * Handles importing Primavera P6 calendars into RDrive via the Calendar API.
 *
 * Responsibilities:
 *  - fetch ALL project calendars (not just the published subset) to decide
 *    create-vs-update by matching the persisted `p6-id:` tag first (rename-proof)
 *    and the P6-derived `code` (case-insensitive) second,
 *  - create new calendars (POST) / update matched ones (PUT) then publish each,
 *  - expose a map of P6 ObjectId -> the resulting RDrive calendar so callers can
 *    re-link imported tasks' timeRanges to real calendar ids.
 *
 * All calendar payloads are camelCase and validated server-side
 * (see CalendarController / Calendar.cs). versionReference must be exactly 8
 * uppercase A–Z/0–9 characters; it identifies a calendar FAMILY and stays the
 * same across that family's revisions, so a fresh one is generated only on
 * create and an update reuses the existing family's.
 *
 * Revisions follow the main web app's calendar editor: a published revision
 * is never edited in place. An update clones the published revision into a
 * draft (POST /calendars/{id}/clone/{code}), replaces the draft's definition
 * (PUT /calendars/{draftId}) and publishes the draft (POST
 * /calendars/{draftId}/publish), which makes it the family's current revision
 * with a new id, repoints the schedules' time-range calendar bridge to it and
 * keeps the previous revision as history. PUT on the published row itself
 * would succeed too, but it overwrites the published definition in place —
 * same id, same versionReference, no revision — which is invisible to anyone
 * reading the calendar's history and never re-derives what depends on a
 * publish.
 *
 * Imported calendars are locked with P6_CALENDAR_KEY so they cannot be edited
 * in RDrive (the API rejects keyless updates to a published+keyed calendar and
 * the UI shows lock.editable=false). The key is set in the POST body on create
 * and passed as the ?key= query param on the draft update — which both
 * authorizes the update and (re)applies the key (a clone does not carry the
 * source's key), so calendars that predate this locking are acquired on
 * their next import.
 */
export const P6_CALENDAR_KEY = 'p6';

export class CalendarImporter {
  constructor(apiClient, projectCode) {
    this.apiClient = apiClient;
    this.projectCode = projectCode;
    // RandomIdGenerator produces exactly the [A-Z0-9]{8} shape the API requires.
    this.idGenerator = new RandomIdGenerator(8);
  }

  /**
   * Generate a fresh versionReference (8 chars, uppercase A–Z / 0–9).
   * @returns {string}
   */
  generateVersionReference() {
    return this.idGenerator.generate();
  }

  /**
   * Fetch every calendar in the project (all revisions). Used for create-vs-update
   * matching, which must consider unpublished calendars too.
   * @returns {Promise<Array>}
   */
  async fetchAllCalendars() {
    const data = await this.apiClient.get(`/api/${this.projectCode}/calendars`);
    if (!data) return [];
    return Array.isArray(data) ? data : Object.values(data);
  }

  /**
   * Fetch the latest PUBLISHED revision of every calendar family. The
   * unfiltered list returns each family's newest row, which is an unpublished
   * draft whenever one exists — so it is the wrong source for the calendars
   * tasks are walked and displayed against.
   * @returns {Promise<Array>}
   */
  async fetchPublishedCalendars() {
    const data = await this.apiClient.get(`/api/${this.projectCode}/calendars?published=true`);
    if (!data) return [];
    return Array.isArray(data) ? data : Object.values(data);
  }

  /**
   * Build the plan of P6 calendars, classifying each as 'create' or 'update'.
   *
   * Matching runs in two claiming passes so no existing calendar is matched by
   * more than one parsed calendar (relevant when a rename gives calendar A
   * another calendar's old name): first by the persisted `p6-id:` tag
   * (rename-proof), then by code over the still-unclaimed remainder.
   *
   * @param {Array} p6Calendars - Parsed P6 calendars from XMLTaskParser
   * @returns {Promise<{entries: Array, unmatchedExisting: Array}>}
   *   entries: { p6Calendar, action, existingCalendar, matchedBy: 'p6Id'|'code'|null }
   *   unmatchedExisting: current P6-owned calendars no parsed calendar claimed —
   *   candidates for a manual re-connect (likely renamed in P6).
   */
  async buildPlan(p6Calendars) {
    const existing = await this.fetchAllCalendars();
    const claimedIds = new Set();

    // Prefer the current revision among calendars sharing a key.
    const claim = (candidates) => {
      const calendar = candidates.find(c => c.current) || candidates[0] || null;
      if (calendar) claimedIds.add(calendar.id);
      return calendar;
    };

    const entries = (p6Calendars || []).map(p6Calendar => ({
      p6Calendar,
      existingCalendar: null,
      action: 'create',
      matchedBy: null
    }));

    // Pass 1: p6-id tag.
    entries.forEach(entry => {
      const { p6ObjectId } = entry.p6Calendar;
      if (!p6ObjectId) return;
      const matches = existing.filter(c => !claimedIds.has(c.id) && getCalendarP6Id(c) === p6ObjectId);
      const calendar = claim(matches);
      if (calendar) {
        entry.existingCalendar = calendar;
        entry.action = 'update';
        entry.matchedBy = 'p6Id';
      }
    });

    // Pass 2: code, over the unclaimed remainder.
    entries.forEach(entry => {
      if (entry.existingCalendar) return;
      const codeLower = (entry.p6Calendar.code || '').toLowerCase();
      if (!codeLower) return;
      const matches = existing.filter(c => !claimedIds.has(c.id) && (c.code || '').toLowerCase() === codeLower);
      const calendar = claim(matches);
      if (calendar) {
        entry.existingCalendar = calendar;
        entry.action = 'update';
        entry.matchedBy = 'code';
      }
    });

    const unmatchedExisting = existing.filter(c =>
      !claimedIds.has(c.id) &&
      c.current &&
      (c.key === P6_CALENDAR_KEY || (c.tags || []).includes('p6-import'))
    );

    return { entries, unmatchedExisting };
  }

  /**
   * Build the CalendarCreatingDTO payload the API expects from a parsed P6 calendar.
   * @param {Object} p6Calendar
   * @param {string|null} [versionReference] - The family's existing
   *   versionReference when updating; a fresh one is generated for a create.
   * @returns {Object}
   */
  buildPayload(p6Calendar, versionReference = null) {
    return {
      title: p6Calendar.title,
      code: p6Calendar.code,
      versionReference: versionReference || this.generateVersionReference(),
      // Windows id matching the zone the activity timestamps are parsed in
      // (see p6TimeZone.js); the working windows are wall-clock in that zone.
      timeZone: p6Calendar.timeZone || P6_TIMEZONE.windowsId,
      hoursPerDay: p6Calendar.hoursPerDay > 0 ? p6Calendar.hoursPerDay : 8,
      daysPerWeek: p6Calendar.daysPerWeek > 0 ? p6Calendar.daysPerWeek : 5,
      daysPerMonth: p6Calendar.daysPerMonth > 0 ? p6Calendar.daysPerMonth : 20,
      // Stamp the source P6 ObjectId as a tag so later imports can match this
      // calendar rename-proof; replace any stale p6-id tag (e.g. after a manual
      // re-connect or an import from a different P6 instance).
      tags: [
        ...(p6Calendar.tags || []).filter(t => !(typeof t === 'string' && t.startsWith(P6_ID_TAG_PREFIX))),
        ...(p6Calendar.p6ObjectId ? [makeP6IdTag(p6Calendar.p6ObjectId)] : [])
      ],
      key: P6_CALENDAR_KEY,
      intervals: (p6Calendar.intervals || []).map(interval => ({ ...interval }))
    };
  }

  /**
   * Whether a CalendarDTO is a published revision (the API exposes both the
   * computed flag and the date it derives from).
   */
  static isPublished(calendar) {
    if (!calendar) return false;
    if (typeof calendar.isPublished === 'boolean') return calendar.isPublished;
    return !!calendar.publishedDate;
  }

  /**
   * Clone a published revision into a new draft revision of the same family
   * (same code and versionReference, new id, unpublished) — the main web
   * app's "new version" action. The API allows one draft per code.
   * @param {Object} calendar - A published CalendarDTO
   * @returns {Promise<Object>} The draft CalendarDTO
   */
  async cloneRevision(calendar) {
    const draft = await this.apiClient.post(
      `/api/${this.projectCode}/calendars/${calendar.id}/clone/${encodeURIComponent(calendar.code)}`,
      {}
    );
    if (!draft || !draft.id) {
      throw new Error('Calendar clone returned no id');
    }
    return draft;
  }

  /**
   * Create or update a single calendar, then publish it. An update becomes a
   * new published revision of the existing family (see the module comment);
   * the returned DTO carries that revision's id, which callers must re-link
   * tasks to.
   * @param {Object} planEntry - { p6Calendar, action, existingCalendar }
   * @param {string} publishMessage
   * @returns {Promise<Object>} The published CalendarDTO (includes id, versionReference)
   */
  async importCalendar(planEntry, publishMessage) {
    const existing = planEntry.action === 'update' ? planEntry.existingCalendar : null;
    const payload = this.buildPayload(planEntry.p6Calendar, existing?.versionReference || null);

    let saved;
    if (existing) {
      // The matched row is the family's latest revision: a published one is
      // cloned into a draft first; an existing draft (someone's unpublished
      // edit, or a previous import that failed before publishing) is reused.
      const draft = CalendarImporter.isPublished(existing) ? await this.cloneRevision(existing) : existing;
      saved = await this.apiClient.put(
        `/api/${this.projectCode}/calendars/${draft.id}?key=${P6_CALENDAR_KEY}`,
        payload
      );
    } else {
      saved = await this.apiClient.post(
        `/api/${this.projectCode}/calendars`,
        payload
      );
    }

    if (!saved || !saved.id) {
      throw new Error('Calendar save returned no id');
    }

    const published = await this.apiClient.post(
      `/api/${this.projectCode}/calendars/${saved.id}/publish`,
      { title: planEntry.p6Calendar.title, message: publishMessage }
    );

    // Prefer the published DTO (carries the final versionReference); fall back to
    // the saved response if publish returns nothing useful.
    return published && published.id ? published : saved;
  }

  /**
   * Import the selected P6 calendars (create/update + publish).
   * Each calendar succeeds/fails independently so one failure does not abort the rest.
   *
   * @param {Array} planEntries - Entries from buildPlan(), pre-filtered to the ones the user included
   * @param {string} publishMessage
   * @returns {Promise<{results: Array, calendarsByP6Id: Map}>}
   *   results: [{ planEntry, status: 'success'|'error', calendar?, error? }]
   *   calendarsByP6Id: Map<p6ObjectId, publishedCalendarDTO> for successful imports
   */
  async importCalendars(planEntries, publishMessage = 'P6 import') {
    const results = [];
    const calendarsByP6Id = new Map();

    for (const entry of planEntries) {
      try {
        const calendar = await this.importCalendar(entry, publishMessage);
        results.push({ planEntry: entry, status: 'success', calendar });
        if (entry.p6Calendar.p6ObjectId) {
          calendarsByP6Id.set(entry.p6Calendar.p6ObjectId, calendar);
        }
      } catch (error) {
        console.error(`Error importing calendar "${entry.p6Calendar.title}":`, error);
        results.push({ planEntry: entry, status: 'error', error });
      }
    }

    return { results, calendarsByP6Id };
  }

  /**
   * Re-link imported tasks to the real RDrive calendar ids for the P6 calendars
   * that were successfully created/updated. Only tracking (non-'none') time ranges
   * are rewritten. The P6 per-activity calendar is a task-specific assignment, so
   * it overrides the schedule-level mapping/fallback the parser resolved — the
   * same precedence the Excel "Calendar Override" column has. Tasks whose P6
   * calendar was excluded/failed keep their existing calendarId (schedule
   * mapping or DEFAULT_CALENDAR_ID).
   *
   * Mutates the tasks in place (they are the payload about to be committed).
   *
   * @param {Array} tasks - Nested task tree
   * @param {Map} taskCalendarLinks - Map<taskId, p6CalendarObjectId>
   * @param {Map} calendarsByP6Id - Map<p6CalendarObjectId, publishedCalendarDTO>
   */
  relinkTasks(tasks, taskCalendarLinks, calendarsByP6Id) {
    if (!taskCalendarLinks || !calendarsByP6Id || calendarsByP6Id.size === 0) return;

    const walk = (nodes) => {
      (nodes || []).forEach(task => {
        const p6ObjectId = taskCalendarLinks.get(task.id);
        const calendar = p6ObjectId ? calendarsByP6Id.get(p6ObjectId) : null;

        if (calendar && calendar.id && Array.isArray(task.timeRanges)) {
          task.timeRanges.forEach(tr => {
            // 'none' time ranges have no scheduling semantics — leave them alone.
            if (tr.timeRangeTemplateType !== 'none') {
              tr.calendarId = calendar.id;
              tr.calendarVersionReference = calendar.versionReference || null;
            }
          });
        }

        if (Array.isArray(task.children) && task.children.length) {
          walk(task.children);
        }
      });
    };

    walk(tasks);
  }
}

/**
 * Human-readable summary of a P6 calendar's working-time intervals, for the
 * confirmation dialog. e.g. "5 working / 2 non-working recurring, 3 holidays".
 * @param {Object} p6Calendar
 * @returns {string}
 */
export function summarizeIntervals(p6Calendar) {
  const intervals = p6Calendar.intervals || [];
  let recurringWorking = 0;
  let recurringNonWorking = 0;
  let staticNonWorking = 0;
  let staticWorking = 0;

  intervals.forEach(iv => {
    const isRecurring = !!iv.recurrentStartDate;
    if (isRecurring) {
      if (iv.isWorking) recurringWorking += 1;
      else recurringNonWorking += 1;
    } else if (iv.isWorking) {
      staticWorking += 1;
    } else {
      staticNonWorking += 1;
    }
  });

  const parts = [];
  if (recurringWorking) parts.push(`${recurringWorking} working day pattern${recurringWorking !== 1 ? 's' : ''}`);
  if (recurringNonWorking) parts.push(`${recurringNonWorking} non-working day pattern${recurringNonWorking !== 1 ? 's' : ''}`);
  if (staticNonWorking) parts.push(`${staticNonWorking} exception${staticNonWorking !== 1 ? 's' : ''}`);
  // Work periods an exception restores on its day (reduced hours, a working weekend).
  if (staticWorking) parts.push(`${staticWorking} exception work period${staticWorking !== 1 ? 's' : ''}`);
  return parts.length ? parts.join(', ') : 'No intervals';
}
